import React, { useState, useEffect } from "react";
import {Mail, Linkedin, Award, Upload, X} from 'lucide-react';
import { lumi } from "../lib/lumi";
import toast from "react-hot-toast";

function AboutM91() {
  const [user, setUser] = useState(lumi.auth.user);
  const [presidentPhoto, setPresidentPhoto] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = lumi.auth.onAuthChange(({ user }) => {
      setUser(user);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    loadPresidentPhoto();
  }, []);

  const loadPresidentPhoto = async () => {
    try {
      const result = await lumi.entities.siteSettings.list({
        filter: { settingKey: "presidentPhoto" }
      });
      if (result.list.length > 0) {
        setPresidentPhoto(result.list[0].settingValue);
      }
    } catch (error) {
      console.error("Failed to load president photo:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    setUploading(true);
    try {
      const uploadResult = await lumi.tools.file.upload([file]);

      if (uploadResult[0].uploadError) {
        toast.error(uploadResult[0].uploadError);
        return;
      }

      const photoUrl = uploadResult[0].fileUrl;
      if (!photoUrl) {
        toast.error("Failed to get photo URL");
        return;
      }

      // Check if setting exists
      const existing = await lumi.entities.siteSettings.list({
        filter: { settingKey: "presidentPhoto" }
      });

      if (existing.list.length > 0) {
        // Update existing
        await lumi.entities.siteSettings.update(existing.list[0]._id, {
          settingValue: photoUrl,
          updatedBy: user?.userId,
          updatedAt: new Date()
        });
      } else {
        // Create new
        await lumi.entities.siteSettings.create({
          settingKey: "presidentPhoto",
          settingValue: photoUrl,
          description: "President's photo on About Us page",
          updatedBy: user?.userId,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }

      setPresidentPhoto(photoUrl);
      toast.success("President photo updated successfully!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!presidentPhoto) return;

    try {
      await lumi.tools.file.delete([presidentPhoto]);

      const existing = await lumi.entities.siteSettings.list({
        filter: { settingKey: "presidentPhoto" }
      });

      if (existing.list.length > 0) {
        await lumi.entities.siteSettings.update(existing.list[0]._id, {
          settingValue: "",
          updatedBy: user?.userId,
          updatedAt: new Date()
        });
      }

      setPresidentPhoto(null);
      toast.success("President photo removed");
    } catch (error) {
      console.error("Remove error:", error);
      toast.error("Failed to remove photo");
    }
  };

  const isAdmin = user?.userRole === "ADMIN";

  return (
    <div className="min-h-screen py-16 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-4 text-black">About MOBA 91</h1>
          <p className="text-gray-600 text-lg">Leadership, Excellence, Brotherhood</p>
        </div>

        {/* President Section */}
        <div className="bg-white border-2 border-[#DC143C]/30 rounded-2xl overflow-hidden mb-12 shadow-xl p-8 md:p-12">
          <div className="grid md:grid-cols-[320px_1fr] gap-8">
            {/* President Photo */}
            <div className="flex justify-center items-start">
              <div className="relative group w-full z-10">
                <div className="absolute inset-0 bg-gradient-to-br from-[#DC143C] to-red-600 rounded-2xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity -z-10"></div>
                <div className="relative w-full aspect-[3/4] bg-gray-100 rounded-2xl border-2 border-[#DC143C]/50 overflow-hidden">
                  {presidentPhoto ?
                  <img
                    src={presidentPhoto}
                    alt="President"
                    className="w-full h-full object-contain" /> :


                  <div className="flex items-center justify-center h-full">
                      <div className="text-center p-8">
                        <div className="w-32 h-32 bg-gradient-to-br from-[#DC143C] to-red-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                          <Award size={48} className="text-white" />
                        </div>
                        <p className="text-gray-600 text-sm font-semibold">President Photo</p>
                        {isAdmin && <p className="text-xs text-gray-500 mt-2">(Click below to upload)</p>}
                      </div>
                    </div>
                  }
                </div>

                {/* Admin Controls */}
                {isAdmin &&
                <div className="mt-4 flex gap-2 relative z-20">
                    <label className="flex-1 cursor-pointer relative z-20">
                      <div className="bg-[#DC143C] hover:bg-red-600 text-white py-2 px-4 rounded-lg text-sm font-semibold transition-all flex items-center justify-center space-x-2 cursor-pointer">
                        {uploading ?
                      <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Uploading...</span>
                          </> :

                      <>
                            <Upload size={16} />
                            <span>{presidentPhoto ? "Change Photo" : "Upload Photo"}</span>
                          </>
                      }
                      </div>
                      <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      disabled={uploading}
                      className="hidden"
                      id="president-photo-upload" />

                    </label>
                    {presidentPhoto &&
                  <button
                    onClick={handleRemovePhoto}
                    className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-all relative z-20 cursor-pointer"
                    title="Remove photo"
                    type="button">

                        <X size={20} />
                      </button>
                  }
                  </div>
                }
              </div>
            </div>

            {/* President Message */}
            <div className="flex flex-col justify-center">
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-black mb-2">Message from the President</h2>
                <div className="h-1 w-20 bg-gradient-to-r from-[#DC143C] to-red-600 rounded-full"></div>
              </div>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Dear MOBA 91 Family,

                </p>
                <p>As we approach our 40th anniversary as Homos, I am filled with immense pride and gratitude for what we have achieved together. Our brotherhood has stood the test of time, embodying the values of excellence, service and unity that Mfantsipim instilled in us.

                </p>
                <p>From the hallowed halls of our alma mater to the various fields where we excel today, MOBA 91 continues to make an indelible mark. Our commitment to giving back to Mfantsipim and supporting each other remains unwavering.

                </p>
                <p>Let us continue to strengthen our bonds, mentor the next generation, and uphold the distinguished legacy of both MOBA 91 and Mfantsipim School.

                </p>
                <p className="text-[#DC143C] font-semibold">
                  Dwen Hwe Kan!
                </p>
              </div>

              <div className="mt-8 pt-6 border-t border-[#DC143C]/20">
                <div className="font-bold text-xl text-black">C. A. Lokko</div>
                <div className="font-semibold text-lg text-[#DC143C]">President</div>
                <div className="text-gray-400 text-sm">Class of 1991</div>
              </div>
            </div>
          </div>
        </div>

        {/* Our Story */}
        <div className="bg-white border-2 border-[#DC143C]/30 rounded-2xl p-8 md:p-12 mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#DC143C]">Our Story</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>MOBA 91 represents the graduating class of 1991 from Mfantsipim School, one of Ghana's most prestigious educational institutions. United by our shared experiences and the school's motto "Dwen Hwe Kan" (Think and Look Ahead), we have built a powerful network of professionals and leaders across various sectors.

            </p>
            <p>
              Our alumni association was founded on principles of brotherhood, excellence, and service. Over the past decades, we have remained committed to supporting our alma mater, mentoring current students, and fostering connections among our members worldwide.
            </p>
            <p>Today, MOBA 91 members are globally distinguished professionals in medicine, law, technology, sports, arts, finance, engineering, education, business and public service, continuing to exemplify the values instilled in us during our formative years at Mfantsipim.

            </p>
          </div>
        </div>

        {/* Vision & Mission */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white border-2 border-[#DC143C]/30 rounded-2xl p-8">
            <h3 className="text-2xl font-bold mb-4 text-[#DC143C]">Our Vision</h3>
            <p className="text-gray-700 leading-relaxed">
              To be a model alumni association that strengthens bonds, promotes excellence, and makes lasting contributions to Mfantsipim School and society at large.
            </p>
          </div>

          <div className="bg-white border-2 border-[#DC143C]/30 rounded-2xl p-8">
            <h3 className="text-2xl font-bold mb-4 text-[#DC143C]">Our Mission</h3>
            <p className="text-gray-700 leading-relaxed">
              To foster lifelong connections among MOBA91 members, support the development of Mfantsipim School, and uphold the values of excellence, integrity, and service in all our endeavors.
            </p>
          </div>
        </div>
      </div>
    </div>);
}

export default AboutM91;